// ===================================================================
// PA GRANTS — Map View
// Spatial view of damage sites and project locations
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import { Group, Button, Text, Paper, Stack, LoadingOverlay } from '@mantine/core';
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Marker,
  Popup,
  LayersControl,
} from 'react-leaflet';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useDisasterFilter } from '../../hooks/useActiveDisaster';
import type { PADamageItem, PAProject, DamageCategory } from '../../types/pagrants';
import {
  DAMAGE_CATEGORY_COLORS,
  DAMAGE_CATEGORY_LABELS,
  PROJECT_PHASE_LABELS,
} from '../../types/pagrants';

// ── Constants ────────────────────────────────────────────────────

const DEFAULT_CENTER: [number, number] = [33.78, -84.38];
const DEFAULT_ZOOM = 11;

const ALL_CATEGORIES: DamageCategory[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

// ── Helpers ──────────────────────────────────────────────────────

function parseCoord(val: string): number | null {
  if (!val || val.trim() === '') return null;
  const n = parseFloat(val);
  return isNaN(n) ? null : n;
}

function fmtCost(val: string): string {
  const n = parseFloat(val);
  if (isNaN(n)) return val || '—';
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/** Create a numbered DivIcon for project markers */
function projectIcon(label: string): L.DivIcon {
  return L.divIcon({
    className: '',
    html: `<div style="
      background:#1E40AF;color:#fff;border-radius:6px;padding:2px 7px;
      font-size:11px;font-weight:700;white-space:nowrap;
      border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,.4);
      display:inline-block;
    ">${label}</div>`,
    iconSize: [40, 24],
    iconAnchor: [20, 12],
  });
}

// ── Component ────────────────────────────────────────────────────

export default function PAMap() {
  const { damageItems: allDamageItems, projects: allProjects, organizations, fetchAll } = usePAGrantsStore();
  const damageItems = useDisasterFilter(allDamageItems);
  const projects = useDisasterFilter(allProjects).filter(p => p.workType !== 'hmgp');
  const [loaded, setLoaded] = useState(false);
  const [activeCategories, setActiveCategories] = useState<Set<DamageCategory>>(new Set(ALL_CATEGORIES));

  useEffect(() => {
    fetchAll().then(() => setLoaded(true));
  }, [fetchAll]);

  // Org lookup map
  const orgMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of organizations) {
      m.set(String(o.id), o.orgName);
    }
    return m;
  }, [organizations]);

  // Parsed damage items with valid coords
  const mappedDamage = useMemo(() => {
    return damageItems.reduce<(PADamageItem & { _lat: number; _lng: number })[]>((acc, d) => {
      const lat = parseCoord(d.lat);
      const lng = parseCoord(d.lng);
      if (lat !== null && lng !== null) {
        acc.push({ ...d, _lat: lat, _lng: lng });
      }
      return acc;
    }, []);
  }, [damageItems]);

  // Parsed projects with valid coords
  const mappedProjects = useMemo(() => {
    return projects.reduce<(PAProject & { _lat: number; _lng: number })[]>((acc, p) => {
      const lat = parseCoord(p.projectLat);
      const lng = parseCoord(p.projectLng);
      if (lat !== null && lng !== null) {
        acc.push({ ...p, _lat: lat, _lng: lng });
      }
      return acc;
    }, []);
  }, [projects]);

  // Filtered damage by category
  const filteredDamage = useMemo(() => {
    return mappedDamage.filter(d => {
      const cat = (d.category || '').toUpperCase() as DamageCategory;
      return activeCategories.has(cat);
    });
  }, [mappedDamage, activeCategories]);

  // Stats
  const totalDamageCost = useMemo(() => {
    return filteredDamage.reduce((sum, d) => sum + (parseFloat(d.estimatedCost) || 0), 0);
  }, [filteredDamage]);
  const totalProjectCost = useMemo(() => {
    return mappedProjects.reduce((sum, p) => sum + (parseFloat(p.estimatedCost) || 0), 0);
  }, [mappedProjects]);

  // Category filter toggle
  function toggleCategory(cat: DamageCategory) {
    setActiveCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }

  return (
    <Stack gap="md">
      {/* Category filter buttons */}
      <Group gap={6}>
        <Text size="sm" fw={600}>Filter by Category:</Text>
        {ALL_CATEGORIES.map(cat => (
          <Button
            key={cat}
            size="xs"
            variant={activeCategories.has(cat) ? 'filled' : 'outline'}
            color={DAMAGE_CATEGORY_COLORS[cat]}
            onClick={() => toggleCategory(cat)}
            styles={{ root: { minWidth: 36 } }}
          >
            {cat}
          </Button>
        ))}
        <Button
          size="xs"
          variant="subtle"
          color="gray"
          onClick={() => setActiveCategories(new Set(ALL_CATEGORIES))}
        >
          All
        </Button>
      </Group>

      {/* Map container */}
      <div style={{ position: 'relative', height: 'calc(100vh - 220px)', minHeight: 500, borderRadius: 8, overflow: 'hidden' }}>
        <LoadingOverlay visible={!loaded} />
        <MapContainer
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />

          <LayersControl position="topright">
            {/* Damage Sites layer */}
            <LayersControl.Overlay checked name="Damage Sites">
              {/* Fragment wrapper so LayersControl sees one child */}
              <>{filteredDamage.map(d => (
                <CircleMarker
                  key={`dmg-${d.id}`}
                  center={[d._lat, d._lng]}
                  radius={7}
                  pathOptions={{
                    color: DAMAGE_CATEGORY_COLORS[d.category as DamageCategory] || '#888',
                    fillColor: DAMAGE_CATEGORY_COLORS[d.category as DamageCategory] || '#888',
                    fillOpacity: 0.7,
                    weight: 2,
                  }}
                >
                  <Popup>
                    <div style={{ minWidth: 200 }}>
                      <strong>{d.facilityName || 'Unnamed Site'}</strong>
                      <br />Type: {d.facilityType || '—'}
                      <br />Category: {DAMAGE_CATEGORY_LABELS[d.category as DamageCategory] || d.category || '—'}
                      <br />Severity: {d.damageSeverity || '—'}
                      <br />Est. Cost: {fmtCost(d.estimatedCost)}
                      <br />Inspection: {d.siteInspected === 'true' || d.siteInspected === 'Yes' ? `Inspected (${d.siteInspectionDate})` : 'Not inspected'}
                    </div>
                  </Popup>
                </CircleMarker>
              ))}</>
            </LayersControl.Overlay>

            {/* Project Sites layer */}
            <LayersControl.Overlay checked name="Project Sites">
              <>{mappedProjects.filter(p => activeCategories.has((p.category || '').toUpperCase() as DamageCategory)).map(p => (
                <Marker
                  key={`proj-${p.id}`}
                  position={[p._lat, p._lng]}
                  icon={projectIcon(p.projectNumber || `#${p.id}`)}
                >
                  <Popup>
                    <div style={{ minWidth: 220 }}>
                      <strong>{p.projectNumber || '—'}: {p.projectTitle || 'Untitled'}</strong>
                      <br />Applicant: {orgMap.get(p.organizationId) || '—'}
                      <br />Category: {DAMAGE_CATEGORY_LABELS[p.category as DamageCategory] || p.category || '—'}
                      <br />Phase: {PROJECT_PHASE_LABELS[p.phase as keyof typeof PROJECT_PHASE_LABELS] || p.phase || '—'}
                      <br />Est. Cost: {fmtCost(p.estimatedCost)}
                      <br />Completion: {p.completionPctEstimate ? `${p.completionPctEstimate}%` : '—'}
                    </div>
                  </Popup>
                </Marker>
              ))}</>
            </LayersControl.Overlay>
          </LayersControl>
        </MapContainer>

        {/* Stats Summary — top-right overlay */}
        <Paper
          shadow="sm"
          p="xs"
          radius="md"
          style={{
            position: 'absolute', top: 12, right: 12, zIndex: 1000,
            background: 'rgba(255,255,255,0.93)', minWidth: 170, pointerEvents: 'auto',
          }}
        >
          <Text size="xs" fw={700} mb={4}>Map Summary</Text>
          <Text size="xs">Projects mapped: <strong>{mappedProjects.length}</strong></Text>
          <Text size="xs">Damage sites: <strong>{filteredDamage.length}</strong></Text>
          <Text size="xs">Project cost: <strong>{fmtCost(String(totalProjectCost))}</strong></Text>
          <Text size="xs">Damage cost: <strong>{fmtCost(String(totalDamageCost))}</strong></Text>
        </Paper>

        {/* Legend — bottom-left overlay */}
        <Paper
          shadow="sm"
          p="xs"
          radius="md"
          style={{
            position: 'absolute', bottom: 24, left: 12, zIndex: 1000,
            background: 'rgba(255,255,255,0.93)', minWidth: 160, pointerEvents: 'auto',
          }}
        >
          <Text size="xs" fw={700} mb={4}>Legend</Text>
          {ALL_CATEGORIES.map(cat => (
            <Group key={cat} gap={6} mb={2}>
              <div style={{
                width: 12, height: 12, borderRadius: '50%',
                background: DAMAGE_CATEGORY_COLORS[cat],
                border: '1px solid #fff', boxShadow: '0 0 0 1px rgba(0,0,0,.15)',
              }} />
              <Text size="xs">{DAMAGE_CATEGORY_LABELS[cat]}</Text>
            </Group>
          ))}
          <Group gap={6} mt={6}>
            <div style={{
              width: 30, height: 16, borderRadius: 4,
              background: '#1E40AF', color: '#fff',
              fontSize: 9, fontWeight: 700, textAlign: 'center', lineHeight: '16px',
            }}>PW</div>
            <Text size="xs">Project Site</Text>
          </Group>
        </Paper>
      </div>
    </Stack>
  );
}
